import type { TFunction } from 'i18next';
import type { ChipTone } from '@cowoo/shared/ui-web';
import type { paths } from '@cowoo/api-client';
import { formatDate } from '@cowoo/shared';

/**
 * How a member row reads (10-web-admin § 10.1).
 *
 * Shared by the list and the panel so the same person is never described two
 * ways on two halves of the same screen.
 */

export type MemberItem =
  paths['/admin/members']['get']['responses']['200']['content']['application/json']['items'][number];

export const CHIPS = [
  'all',
  'flex_desk',
  'private_desk',
  'office',
  'day_ticket_only',
  'payment_open',
  'cancelled',
] as const;
export type MemberChip = (typeof CHIPS)[number];

export function memberName(member: Pick<MemberItem, 'firstName' | 'lastName' | 'email'>): string {
  return [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;
}

/** "FlexDesk", "PrivateDesk P1", "Büro 2", "Tagesticket". */
export function tariffOf(member: MemberItem, t: TFunction): string {
  if (!member.productKey) return t('admin.members.chip.day_ticket_only');
  const product = t(`admin.members.product.${member.productKey}`);
  return member.resourceLabel ? `${product} ${member.resourceLabel}` : product;
}

/**
 * The status column of § 10.1, in the order it has to be checked.
 *
 * Blocked first: a blocked member whose invoice is also open is blocked, and
 * that is the fact staff act on. Then the money, then the cancellation, then
 * "aktiv" — and "kein Vertrag" for somebody who has never had one.
 */
export function statusOf(member: MemberItem): {
  key: string;
  tone: ChipTone;
  values?: Record<string, string>;
} {
  if (member.accessBlocked) return { key: 'blocked', tone: 'danger' };
  if (member.openInvoices > 0) return { key: 'payment_open', tone: 'warn' };
  if (member.endsOn) {
    return { key: 'cancelled', tone: 'danger', values: { date: formatDate(member.endsOn) } };
  }
  if (!member.productKey) return { key: 'no_contract', tone: 'neutral' };
  return { key: 'active', tone: 'success' };
}
